import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { HardDrive } from 'lucide-react';

export const StorageWidget = ({ scope = 'me', eventId = null, testid = 'storage-widget' }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let url = '/storage/me';
    if (scope === 'event' && eventId) url = `/events/${eventId}/storage`;
    api.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [scope, eventId]);
  if (!data) return null;
  const used = data.used_bytes || 0;
  const quota = data.quota_bytes || data.quota_bytes_per_event || 0;
  const pct = quota ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const warn = pct >= 80;
  const block = pct >= 100;

  const percentTextColor = (() => {
    if (block) return 'text-destructive';
    if (warn) return 'text-[hsl(var(--warning,38_92%_50%))]';
    return 'text-[hsl(var(--primary))]';
  })();
  const barColor = (() => {
    if (block) return 'bg-destructive';
    if (warn) return 'bg-amber-500';
    return 'gradient-fab';
  })();

  return (
    <Card data-testid={testid} className="rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center"><HardDrive className="h-4 w-4" /></div>
          <div>
            <div className="text-sm font-semibold">{scope === 'event' ? 'Event storage' : 'My uploads'}</div>
            <div className="text-xs text-muted-foreground">{formatBytes(used)} {quota ? `of ${formatBytes(quota)}` : ''} {data.media_count != null ? `· ${data.media_count} items` : ''}</div>
          </div>
        </div>
        <div data-testid={`${testid}-percent`} className={`text-sm font-bold ${percentTextColor}`}>{pct}%</div>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {warn && (
        <div data-testid={`${testid}-warning`} className={`text-xs mt-2 ${block ? 'text-destructive' : 'text-amber-600'}`}>
          {block ? 'Storage is full. Free up space or remove media to upload more.' : 'Getting close to capacity (80%+). Consider tidying up older media.'}
        </div>
      )}
    </Card>
  );
};

export function formatBytes(b) {
  if (b == null || isNaN(b)) return '0 B';
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}
